/**
 * Settings API Service
 * Provides data for pricing rules and settings, switching between mock and real APIs
 * based on the USE_MOCK_DATA environment variable
 */

import { USE_MOCK_DATA, mockApiService } from './mockData';
import api from './api';
import { API_ENDPOINTS } from '../../config/api';

/**
 * Convert decimal weights (0.0-1.0) to percentages (0-100)
 * @param {Object} weights - Object with decimal weight values
 * @returns {Object} Object with percentage weight values
 */
function convertDecimalsToPercentages(weights) {
  if (!weights) return {};
  return Object.fromEntries(
    Object.entries(weights).map(([k, v]) => [k, Math.round(v * 100)])
  );
}

/**
 * Convert percentage weights (0-100) to decimals (0.0-1.0)
 * @param {Object} weights - Object with percentage weight values
 * @returns {Object} Object with decimal weight values
 */
function convertPercentagesToDecimals(weights) {
  if (!weights) return {};
  return Object.fromEntries(
    Object.entries(weights).map(([k, v]) => [k, v / 100])
  );
}

/**
 * Settings API that automatically uses mock or real data
 */
export const settingsApi = {
  // ============================================
  // Tenant Pricing Rules (for regular users)
  // ============================================

  /**
   * Get similarity weights and division weights for the tenant
   * @returns {Promise<{
   *   similarityWeights: { weights: Object, metrics: Array, isCustomized: boolean },
   *   divisionWeights: { weights: Object, vocabulary: Object, isCustomized: boolean }
   * }>}
   */
  getPricingRules: async () => {
    if (USE_MOCK_DATA) {
      return mockApiService.getPricingRules();
    }
    const response = await api.get(API_ENDPOINTS.PRICING_RULES);
    // API returns { success, data, meta } - extract the data payload
    const responseData = response.data || response;

    // Convert similarity weights from decimals to percentages for UI
    return {
      similarityWeights: {
        ...responseData.similarityWeights,
        weights: convertDecimalsToPercentages(responseData.similarityWeights?.weights),
        // Also convert default weights in metrics
        metrics: responseData.similarityWeights?.metrics?.map(m => ({
          ...m,
          defaultWeight: Math.round((m.defaultWeight || 0) * 100),
        })) || [],
      },
      divisionWeights: {
        ...responseData.divisionWeights,
        // Division weights stay as decimals (0.0-1.0) for consistency with vocabulary
        // The UI will display as percentages but store as decimals
      },
    };
  },

  /**
   * Update tenant's pricing rules
   * @param {Object} updateData - Update data
   * @param {Object} [updateData.similarityWeights] - Similarity weights as percentages (0-100, must sum to 100)
   * @param {Object} [updateData.divisionWeights] - Division field weights as decimals (0.0-1.0)
   * @returns {Promise<{success: boolean, similarityWeights: Object, divisionWeights: Object}>}
   */
  updatePricingRules: async (updateData) => {
    if (USE_MOCK_DATA) {
      return mockApiService.updatePricingRules(updateData);
    }

    // Convert similarity weights from percentages to decimals for API
    const payload = {};
    if (updateData.similarityWeights) {
      payload.similarityWeights = convertPercentagesToDecimals(updateData.similarityWeights);
    }
    if (updateData.divisionWeights) {
      // Division weights are already decimals
      payload.divisionWeights = updateData.divisionWeights;
    }

    const response = await api.put(API_ENDPOINTS.PRICING_RULES, payload);
    // API returns { success, data, meta } - extract the data payload
    const responseData = response.data || response;

    // Convert response back to percentages for UI
    return {
      ...responseData,
      similarityWeights: {
        ...responseData.similarityWeights,
        weights: convertDecimalsToPercentages(responseData.similarityWeights?.weights),
      },
    };
  },

  /**
   * List per-tenant line-item description -> MasterFormat code mappings.
   * Returns the raw mappings array from the API.
   * @returns {Promise<{ mappings: Array<{
   *   normalizedDescription: string,
   *   originalDescription: string,
   *   masterformatCode: string,
   *   masterformatTitle: string|null,
   *   createdBy: string|null,
   *   createdAt: string|null,
   *   updatedAt: string|null,
   *   sourceEstimateId: string|null,
   * }>}>}
   */
  listLineItemMappings: async () => {
    if (USE_MOCK_DATA) {
      return { mappings: [] };
    }
    const response = await api.get(API_ENDPOINTS.LINE_ITEM_MAPPINGS);
    const responseData = response.data || response;
    return { mappings: responseData.mappings || [] };
  },

  /**
   * Delete a single saved mapping by its normalized description.
   * @param {string} normalizedDescription
   * @returns {Promise<{success: boolean}>}
   */
  deleteLineItemMapping: async (normalizedDescription) => {
    if (USE_MOCK_DATA) {
      return { success: true };
    }
    const response = await api.delete(
      API_ENDPOINTS.LINE_ITEM_MAPPING_DETAIL(normalizedDescription),
    );
    const responseData = response.data || response;
    return { success: responseData.success !== false };
  },

  /**
   * Reset tenant's pricing rules to defaults
   * @returns {Promise<{success: boolean, similarityWeights: Object, divisionWeights: Object}>}
   */
  resetPricingRules: async () => {
    if (USE_MOCK_DATA) {
      return mockApiService.resetPricingRules();
    }
    const response = await api.delete(API_ENDPOINTS.PRICING_RULES);
    // API returns { success, data, meta } - extract the data payload
    const responseData = response.data || response;

    // Convert response back to percentages for UI
    return {
      ...responseData,
      similarityWeights: {
        ...responseData.similarityWeights,
        weights: convertDecimalsToPercentages(responseData.similarityWeights?.weights),
        metrics: responseData.similarityWeights?.metrics?.map(m => ({
          ...m,
          defaultWeight: Math.round((m.defaultWeight || 0) * 100),
        })) || [],
      },
    };
  },

};

/**
 * Vocabulary settings API client.
 *
 * Backed by the Lambdas under /backend/python/lambdas/{list,update,delete,merge,
 * list_vocabulary_suggestions, dismiss_vocabulary_suggestion}_vocabulary*. All
 * routes are tenant-scoped via Cognito auth context.
 */
/**
 * Jobber integration API client.
 *
 * Backed by the four lambdas under /backend/python/lambdas/jobber_*. The
 * connect endpoint returns the Jobber OAuth authorize URL the browser then
 * navigates to; the callback is handled server-side and ends with a 302
 * back to the frontend.
 */
export const jobberApi = {
  /** Returns { authorizeUrl, state }. The caller should redirect the browser to authorizeUrl. */
  connect: async () => {
    const res = await api.post('/integrations/jobber/connect', {});
    return res.data || res;
  },

  /** Returns the connection status object (connected, jobberAccountId, etc.). */
  status: async () => {
    const res = await api.get('/integrations/jobber/status');
    return res.data || res;
  },

  /** Disconnect (admin role required on backend). */
  disconnect: async () => {
    return api.delete('/integrations/jobber');
  },

  /** Enqueue a historical-pull sync. Returns { syncId, status: 'queued' }. */
  sync: async () => {
    const res = await api.post('/integrations/jobber/sync', {});
    return res.data || res;
  },
};

export const vocabularyApi = {
  list: async () => {
    const res = await api.get('/settings/vocabulary');
    return (res.data || res).entries || [];
  },

  update: async (vocabEntryId, { canonicalName, csiDivisionCode } = {}) => {
    const body = {};
    if (canonicalName !== undefined) body.canonical_name = canonicalName;
    if (csiDivisionCode !== undefined) body.csi_division_code = csiDivisionCode;
    const res = await api.patch(`/settings/vocabulary/${vocabEntryId}`, body);
    return res.data || res;
  },

  delete: async (vocabEntryId) => {
    return api.delete(`/settings/vocabulary/${vocabEntryId}`);
  },

  merge: async (survivingId, mergedIds) => {
    const res = await api.post('/settings/vocabulary/merge', {
      surviving_id: survivingId,
      merged_ids: mergedIds,
    });
    return res.data || res;
  },

  listSuggestions: async () => {
    const res = await api.get('/settings/vocabulary/suggestions');
    return (res.data || res).suggestions || [];
  },

  dismissSuggestion: async (suggestionId) => {
    return api.post(`/settings/vocabulary/suggestions/${suggestionId}/dismiss`);
  },
};

export default settingsApi;
